import { faTimes } from '@fortawesome/free-solid-svg-icons/faTimes'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import IconButton from '@mui/material/IconButton'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { useTheme } from '@mui/material/styles'
import { getProfileSrc } from '@tm/client-app/src/common/image'
import useI18N from '@tm/client-form/src/hooks/useI18n'
import { imagePlaceholder, imagePlaceholderBuilding } from '@tm/client-form/src/misc/imagePlaceholder'
import { CustomValue } from '@tm/shared-lib/src/api'
import { FontAwesomeSvgIcon } from '@tm/ui/composite/FontAwesomeSvgIcon'

import { ImageInputProps } from './types'

const ContactInfoPicturePreview = (props: { file?: File; value?: CustomValue; placeholder: string }) => {
  const preview = props.file
    ? URL.createObjectURL(props.file)
    : typeof props.value === 'string' && props.value
    ? getProfileSrc(props.value)
    : props.placeholder
  return <img src={preview} />
}

export function ProfileImageInput(props: ImageInputProps) {
  const { parentProps, fileFieldState, file, fileTypes } = props
  const { value, isSubmitting, field } = parentProps
  const { t } = useI18N()
  const theme = useTheme()

  const { onFileChange, clearFieldValue, maxSizeText, dragOver, onDragOver, onDragLeave, onDrop } = fileFieldState

  const hasValue = !!file || !!value
  return (
    <Stack gap={1} data-testid="profile-image-input">
      <Stack
        direction="row"
        gap={1}
        alignItems="center"
        sx={{
          bgcolor: 'rgba(255, 255, 255, .8)',
          borderRadius: 1,
          boxShadow: dragOver ? `inset 0 0 0 2px ${theme.palette.info.main}` : theme.customShadows.card,
          px: 2,
          height: 64,
        }}
        onDragEnter={onDragOver}
        onDragLeave={onDragLeave}
        onDragOver={onDragOver}
        onDrop={onDrop}>
        <Box
          sx={{
            position: 'relative',
            width: 40,
            height: 40,
            borderRadius: '50%',
            bgcolor: 'grey.200',
            border: theme => `1px solid ${theme.palette.divider}`,
            img: {
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              borderRadius: '50%',
              objectFit: 'cover',
            },
          }}>
          <ContactInfoPicturePreview
            value={value}
            file={file}
            placeholder={field.name === 'companyLogo' ? imagePlaceholderBuilding : imagePlaceholder}
          />
        </Box>
        <Box
          sx={{
            position: 'relative',
            cursor: 'pointer',
            '&:hover .MuiButton-outlined': {
              boxShadow: theme => theme.customShadows.z4,
            },
          }}>
          <Button variant="outlined" color="inherit" disabled={isSubmitting}>
            {t('file.upload')}
          </Button>
          <Box
            component="input"
            type="file"
            onChange={onFileChange}
            accept=".jpg,.jpeg,.png,.gif,.wepm"
            id="picture-input"
            sx={{
              position: 'absolute',
              top: 0,
              right: 0,
              bottom: 0,
              left: 0,
              cursor: 'pointer',
              opacity: 0,
            }}
          />
        </Box>
        {hasValue ? (
          <IconButton onClick={clearFieldValue} size="small">
            <FontAwesomeSvgIcon icon={faTimes} />
          </IconButton>
        ) : (
          <Typography variant="caption" color="text.secondary">
            {fileTypes} / max. {maxSizeText}mb
          </Typography>
        )}
      </Stack>
    </Stack>
  )
}
